import { createRouter, createWebHistory } from 'vue-router'
import HomePage from '@/views/HomePage.vue'
import Welcome from '@/views/Welcome.vue'
import Chat from '@/views/Chat.vue'
import Admin from '@/views/Admin.vue'
import OAuthCallback from '@/views/OAuthCallback.vue'
import store from '@/store'
import { ROUTES } from '@/constants'
// import TermsOfUse from '@/views/TermsOfUse.vue'
// import PrivacyPolicy from '@/views/PrivacyPolicy.vue'

const routes = [
  {
    path: ROUTES.HOME,
    name: 'Home',
    component: HomePage,
    meta: {
      title: 'We Search Research — AI-powered clinical trial matching',
      description: 'Discover clinical trials, check if you may qualify, and connect with research teams — through a free, secure AI search assistant.'
    }
  },
  {
    path: ROUTES.WELCOME,
    name: 'Welcome',
    component: Welcome,
    meta: {
      requiresAuth: true,
      title: 'Welcome',
      robots: 'noindex,nofollow'
    }
  },
  {
    path: ROUTES.CHAT,
    name: 'Chat',
    component: Chat,
    meta: {
      requiresAuth: true,
      title: 'Chat',
      robots: 'noindex,nofollow'
    }
  },
  {
    path: ROUTES.ADMIN,
    name: 'Admin',
    component: Admin,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Admin Panel',
      robots: 'noindex,nofollow'
    }
  },
  {
    path: ROUTES.PROFILE,
    name: 'Profile',
    component: () => import('@/views/Profile.vue'),
    meta: {
      requiresAuth: true,
      title: 'My Profile',
      robots: 'noindex,nofollow'
    }
  },
  {
    path: ROUTES.OAUTH_CALLBACK,
    name: 'OAuthCallback',
    component: OAuthCallback,
    meta: {
      title: 'Signing In',
      robots: 'noindex,nofollow'
    }
  },
  {
    path: ROUTES.RESET_PASSWORD,
    name: 'ResetPassword',
    component: () => import('@/views/ResetPassword.vue'),
    meta: {
      title: 'Reset Password',
      robots: 'noindex,nofollow'
    }
  },
  {
    path: ROUTES.RESEARCHER_VERIFICATION,
    name: 'ResearcherVerification',
    component: () => import('@/views/ResearcherVerification.vue'),
    meta: {
      title: 'Researcher Verification',
      robots: 'noindex,nofollow'
    }
  },
  {
    path: ROUTES.RESEARCHER_LOGIN,
    name: 'ResearcherLogin',
    component: () => import('@/views/ResearcherLogin.vue'),
    meta: {
      title: 'Researcher Login',
      robots: 'noindex,nofollow'
    }
  },
  {
    path: ROUTES.ABOUT_US,
    name: 'AboutUs',
    component: () => import('@/views/AboutUs.vue'),
    meta: {
      title: 'About Us',
      description: 'We Search Research is on a mission to make clinical trials more accessible and easier to understand for patients and caregivers.'
    }
  },
  {
    path: ROUTES.BECOME_PARTNER,
    name: 'BecomePartner',
    component: () => import('@/views/BecomePartner.vue'),
    meta: {
      title: 'Become a Partner',
      description: 'Partner with We Search Research to reach motivated patients, accelerate clinical trial enrollment, and modernize patient recruitment.'
    }
  },
  {
    path: ROUTES.FAQ,
    name: 'FAQ',
    component: () => import('@/views/FAQ.vue'),
    meta: {
      title: 'Clinical Trial FAQ',
      description: 'Answers to common questions about clinical trials: what they are, how long they take, safety, compensation, and what happens after you apply.'
    }
  },
  {
    path: ROUTES.HOW_IT_WORKS,
    name: 'HowItWorks',
    component: () => import('@/views/HowItWorks.vue'),
    meta: {
      title: 'How It Works',
      description: 'Three simple steps to find a clinical trial: tell us what you\'re looking for, answer a few questions, and connect with the research team.'
    }
  },
  {
    path: ROUTES.RESEARCHER_DASHBOARD,
    name: 'ResearcherDashboard',
    component: () => import('@/views/ResearcherDashboard.vue'),
    meta: {
      title: 'Researcher Dashboard',
      requiresResearcherAuth: true,
      robots: 'noindex,nofollow'
    }
  },
  {
    path: ROUTES.PATIENT_SUMMARY,
    name: 'PatientSummaryView',
    component: () => import('@/views/PatientSummaryView.vue'),
    meta: {
      title: 'Patient Summary',
      requiresResearcherAuth: true,
      robots: 'noindex,nofollow'
    }
  },
  {
    path: ROUTES.ADMIN_RESEARCHERS,
    name: 'ResearcherManagement',
    component: () => import('@/views/admin/ResearcherManagement.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Researcher Management',
      robots: 'noindex,nofollow'
    }
  },
  {
    path: ROUTES.TERMS_OF_USE,
    name: 'TermsOfUse',
    component: () => import('@/views/TermsOfUse.vue'),
    meta: {
      requiresAuth: false,
      requiresAdmin: false,
      title: 'Terms of Use',
      description: 'Terms of Use for the We Search Research clinical trial matching platform.'
    }
  },
  {
    path: ROUTES.PRIVACY_POLICY,
    name: 'PrivacyPolicy',
    component: () => import('@/views/PrivacyPolicy.vue'),
    meta: {
      requiresAuth: false,
      requiresAdmin: false,
      title: 'Privacy Policy',
      description: 'How We Search Research collects, uses, and protects your personal and health information.'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: ROUTES.HOME
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

router.beforeEach(async (to, from, next) => {
  const isAuthenticated = store.getters['auth/isAuthenticated']

  // If we have a token but no user, initialize auth first
  if (isAuthenticated && !store.getters['auth/currentUser']) {
    try {
      await store.dispatch('auth/initializeAuth')
    } catch (error) {
      // Auth initialization failed, continue with current state
    }
  }

  const isAdmin = store.getters['auth/isAdmin']

  if (to.meta.title) {
    // Fallback title set immediately; useSeo() in views will refine it
    // (and append the brand suffix) once the component mounts.
    document.title = to.meta.title
  }

  // Sync <meta name="robots"> with the destination route so noindex routes
  // (chat, admin, researcher, auth callbacks, patient-summary tokens, etc.)
  // are never accidentally indexed if a crawler executes the SPA.
  try {
    let robotsTag = document.querySelector('meta[name="robots"]')
    if (!robotsTag) {
      robotsTag = document.createElement('meta')
      robotsTag.setAttribute('name', 'robots')
      document.head.appendChild(robotsTag)
    }
    robotsTag.setAttribute('content', to.meta.robots || 'index,follow,max-image-preview:large')
  } catch (_) { /* SSR / no-DOM contexts */ }

  if (to.meta.requiresAuth && !isAuthenticated) {
    next(ROUTES.HOME)
  } else if (to.meta.requiresAdmin && !isAdmin) {
    // User is authenticated but not an admin
    next(ROUTES.HOME)
  } else if (to.meta.requiresResearcherAuth) {
    if (!isAuthenticated) {
      next({
        path: ROUTES.RESEARCHER_LOGIN,
        query: {
          redirect: to.fullPath,
        }
      })
    } else {
      next()
    }
  } else {
    next()
  }
})

export default router
